/**
 * Composable for guided onboarding tours.
 *
 * Module-level state persists across component mounts/unmounts.
 * Task event bus subscription is set up once on first call.
 * Supports multiple tours via tourId + step definitions.
 */
import { computed, reactive, readonly, nextTick } from "vue";
import posthog from "posthog-js";
import router from "@/router";
import { useTasksStore } from "@/stores/tasks";
import { useUserStore } from "@/stores/user";
import { TaskEventE, TaskBusEvent } from "@/types/general";
import { isTaskTerminated, TaskStatusE } from "@/types/api/tasks";
import type { OnboardingState } from "@/types/user";
import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";

const tourCompletionKeys: Record<string, keyof OnboardingState> = {
	QuickStart: "Tour_QuickStart_Completed",
	Detailed: "Tour_Detailed_Completed",
};

export const TourStepTypeE = {
	Modal: "modal",
	Tooltip: "tooltip",
} as const;

export type TourStepType = (typeof TourStepTypeE)[keyof typeof TourStepTypeE];

interface TourStepBase {
	type: TourStepType;
	title: string;
	text: string;
	mobileOnly?: boolean;
	beforeShow?: () => void | boolean | Promise<void | boolean>;
	onNext?: () => void | Promise<void>;
}

export interface TourModalStep extends TourStepBase {
	type: typeof TourStepTypeE.Modal;
}

export interface TourTooltipStep extends TourStepBase {
	type: typeof TourStepTypeE.Tooltip;
	targetSelector: string;
	interactive?: boolean;
	tooltipLocation?: string;
	waitForRoute?: string;
	waitForClickSelector?: string;
	waitForTaskCompletion?: boolean;
}

export type TourStep = TourModalStep | TourTooltipStep;

const tourState = reactive({
	isActive: false,
	activeTourId: null as string | null,
	steps: [] as TourStep[],
	currentStepIndex: 0,
	startTime: null as number | null,
	waitingForTaskCompletion: false,
	taskFailed: false,
	showLeaveConfirm: false,
	tourNavigating: false,
	stepEnteredAt: null as number | null,
	pendingNavigation: null as {
		to: RouteLocationNormalized;
		next: NavigationGuardNext;
	} | null,
});

let activeCleanups: (() => void)[] = [];

function cleanupListeners() {
	activeCleanups.forEach((fn) => fn());
	activeCleanups = [];
}

function isMobile(): boolean {
	return window.innerWidth < 960;
}

function getVisibleSteps(steps: TourStep[]): TourStep[] {
	const mobile = isMobile();
	return steps.filter((step) => !step.mobileOnly || mobile);
}

let eventBusSubscribed = false;

function subscribeToTaskEventBus() {
	if (eventBusSubscribed) return;
	eventBusSubscribed = true;

	const tasksStore = useTasksStore();
	tasksStore.taskEventBus.on((event: TaskBusEvent) => {
		if (!tourState.isActive || !tourState.waitingForTaskCompletion) return;

		const currentStep = tourState.steps[tourState.currentStepIndex];
		if (currentStep?.type !== TourStepTypeE.Tooltip) return;

		const ts = currentStep as TourTooltipStep;
		if (!ts.interactive && !ts.waitForTaskCompletion) return;

		if (event.type === TaskEventE.TaskCompleted && event.taskData.Status === TaskStatusE.Completed) {
			const taskCreatedAt = event.taskData.CreatedAt ? new Date(event.taskData.CreatedAt).getTime() : Date.now();
			if (tourState.stepEnteredAt && taskCreatedAt < tourState.stepEnteredAt) return;

			tourState.waitingForTaskCompletion = false;
			tourState.taskFailed = false;

			/*
			 *	interactive: auto-advance on completion
			 *	waitForTaskCompletion: just enable the Next button
			 */
			if (ts.interactive) {
				advanceToNextVisibleStep();
			}
		} else if (isTaskTerminated(event.taskData.Status)) {
			tourState.taskFailed = true;
			tourState.waitingForTaskCompletion = false;
		}
	});
}

function setupStepListeners() {
	cleanupListeners();

	const step = tourState.steps[tourState.currentStepIndex];
	if (!step || step.type !== TourStepTypeE.Tooltip) return;

	const tooltipStep = step as TourTooltipStep;

	if (tooltipStep.waitForRoute) {
		const targetRoute = tooltipStep.waitForRoute;
		const unwatch = router.afterEach((to) => {
			if (to.path === targetRoute) {
				/* Delay to let the page render before advancing */
				setTimeout(() => {
					advanceToNextVisibleStep();
				}, 300);
			}
		});
		activeCleanups.push(unwatch);
	}

	if (tooltipStep.waitForClickSelector) {
		const selector = tooltipStep.waitForClickSelector;
		const checkAndListen = () => {
			const el = document.querySelector(selector);
			if (el) {
				const handler = () => {
					el.removeEventListener("click", handler);
					setTimeout(() => {
						advanceToNextVisibleStep();
					}, 300);
				};
				el.addEventListener("click", handler);
				activeCleanups.push(() => el.removeEventListener("click", handler));
			} else {
				/* Element may not be in DOM yet (e.g., inside a dialog that's still opening) */
				const observer = new MutationObserver(() => {
					const target = document.querySelector(selector);
					if (target) {
						observer.disconnect();
						const handler = () => {
							target.removeEventListener("click", handler);
							setTimeout(() => {
								advanceToNextVisibleStep();
							}, 300);
						};
						target.addEventListener("click", handler);
						activeCleanups.push(() => target.removeEventListener("click", handler));
					}
				});
				observer.observe(document.body, { childList: true, subtree: true });
				activeCleanups.push(() => observer.disconnect());
			}
		};
		checkAndListen();
	}
}

function advanceToNextVisibleStep() {
	cleanupListeners();
	const mobile = isMobile();

	let nextIndex = tourState.currentStepIndex + 1;
	while (nextIndex < tourState.steps.length) {
		const step = tourState.steps[nextIndex];
		if (!step.mobileOnly || mobile) break;
		nextIndex++;
	}

	tourState.currentStepIndex = nextIndex;
	tourState.stepEnteredAt = Date.now();

	const nextStep = tourState.steps[tourState.currentStepIndex];
	if (!nextStep) return;

	if (nextStep.type === TourStepTypeE.Tooltip) {
		const ts = nextStep as TourTooltipStep;
		if (ts.interactive || ts.waitForTaskCompletion) {
			tourState.waitingForTaskCompletion = true;
			tourState.taskFailed = false;
		}
		if (ts.waitForRoute || ts.waitForClickSelector) {
			setupStepListeners();
		}
	}
}

export function useTour() {
	subscribeToTaskEventBus();

	const currentStep = computed((): TourStep | undefined => {
		return tourState.steps[tourState.currentStepIndex];
	});

	const visibleStepCount = computed((): number => {
		return getVisibleSteps(tourState.steps).length;
	});

	const visibleStepNumber = computed((): number => {
		const mobile = isMobile();
		let count = 0;
		for (let i = 0; i <= tourState.currentStepIndex && i < tourState.steps.length; i++) {
			if (!tourState.steps[i].mobileOnly || mobile) {
				count++;
			}
		}
		return count;
	});

	function start(tourId: string, steps: TourStep[]) {
		cleanupListeners();
		tourState.isActive = true;
		tourState.activeTourId = tourId;
		tourState.steps = steps;
		tourState.currentStepIndex = 0;
		tourState.startTime = Date.now();
		tourState.waitingForTaskCompletion = false;
		tourState.taskFailed = false;
		tourState.showLeaveConfirm = false;
		tourState.tourNavigating = false;
		tourState.stepEnteredAt = Date.now();
		tourState.pendingNavigation = null;

		const firstStep = steps[0];
		if (firstStep?.mobileOnly && !isMobile()) {
			advanceToNextVisibleStep();
		}
	}

	async function next() {
		const step = tourState.steps[tourState.currentStepIndex];
		if (!step) return;

		/* Fire started event on first visible step */
		if (tourState.currentStepIndex === 0) {
			posthog?.capture(`started_${tourState.activeTourId}_tour`);
		}

		if (step.onNext) {
			tourState.tourNavigating = true;
			await step.onNext();
			await nextTick();
			tourState.tourNavigating = false;
		}

		advanceToNextVisibleStep();
	}

	async function saveTourCompletion() {
		const userStore = useUserStore();
		const stateKey = tourState.activeTourId ? tourCompletionKeys[tourState.activeTourId] : undefined;
		if (!stateKey) return;
		try {
			await userStore.syncOnboardingStateWithRemote({
				[stateKey]: true,
			});
		} catch {
			/* Best-effort — tour works without persisting completion */
		}
	}

	async function skip() {
		cleanupListeners();
		posthog?.capture(`skipped_${tourState.activeTourId}_tour`, {
			step: tourState.currentStepIndex + 1,
		});
		await saveTourCompletion();
		deactivate();
	}

	async function finish() {
		cleanupListeners();
		posthog?.capture(`${tourState.activeTourId}_tour_completed`, {
			date: new Date().toISOString(),
			duration: tourState.startTime ? Date.now() - tourState.startTime : 0,
		});
		await saveTourCompletion();
		deactivate();
	}

	function deactivate() {
		cleanupListeners();
		document.querySelector(".tour-target-line")?.classList.remove("tour-target-line");
		tourState.isActive = false;
		tourState.activeTourId = null;
		tourState.steps = [];
		tourState.currentStepIndex = 0;
		tourState.startTime = null;
		tourState.waitingForTaskCompletion = false;
		tourState.taskFailed = false;
		tourState.showLeaveConfirm = false;
		tourState.tourNavigating = false;
		tourState.stepEnteredAt = null;
		tourState.pendingNavigation = null;
	}

	function confirmLeave(to: RouteLocationNormalized, nextFn: NavigationGuardNext) {
		if (tourState.tourNavigating) {
			nextFn();
			return;
		}

		/* If the current step expects this route change, allow it */
		const step = tourState.steps[tourState.currentStepIndex];
		if (step?.type === TourStepTypeE.Tooltip && (step as TourTooltipStep).waitForRoute === to.path) {
			nextFn();
			return;
		}

		tourState.pendingNavigation = { to, next: nextFn };
		tourState.showLeaveConfirm = true;
	}

	function cancelLeave() {
		tourState.showLeaveConfirm = false;
		if (tourState.pendingNavigation) {
			tourState.pendingNavigation.next(false);
			tourState.pendingNavigation = null;
		}
	}

	function proceedLeave() {
		tourState.showLeaveConfirm = false;
		const pending = tourState.pendingNavigation;
		skip();
		if (pending) {
			pending.next();
		}
	}

	return {
		tourState: readonly(tourState),
		currentStep,
		visibleStepCount,
		visibleStepNumber,
		start,
		next,
		skip,
		finish,
		confirmLeave,
		cancelLeave,
		proceedLeave,
	};
}
